/**
 * Экраны механик, у которых вход не через скан кода: квиз, ежедневный вход и
 * приглашения.
 *
 * Поведение и тексты — из прототипа `prizma-games.html`, но вся арифметика
 * пришла с сервера: число верных ответов, награда за день серии и засчитанные
 * приглашения считаются там. Здесь только показ — иначе цифра на экране и
 * начисление в балансе однажды разойдутся.
 */
import type { InviteView, QuizResult, QuizView, StreakView } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import { Badge, Button, Card, Empty, Icon, KeyRow, Skeleton, num } from '@allswipes/ui';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api } from '../api';
import { linkFor } from '../link';
import { Shell } from '../components/Shell';
import { useResource } from '../hooks';
import { useSession } from '../session';
import { useToast } from '../toast';

import { usePlural, useT } from '@allswipes/i18n';

/** Приглашение войти — механики без кода требуют аккаунта. */
function NeedSignIn({ title, children }: { title: string; children: string }) {
  const t = useT();
  return (
    <Shell title={title}>
      <Empty icon="user" title={t('Нужен вход')}>
        {children}
      </Empty>
      <Link to="/login" className="btn pri block" style={{ marginTop: 14 }}>
        {t('Войти по почте')}
      </Link>
    </Shell>
  );
}

// ─── квиз ───

export function QuizScreen() {
  const plural = usePlural();
  const t = useT();
  const { id = '' } = useParams();
  const { signedIn } = useSession();
  const toast = useToast();
  const view = useResource(() => api.quizView(id), [id]);

  const [started, setStarted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [picks, setPicks] = useState<number[]>([]);
  const [result, setResult] = useState<QuizResult | null>(null);
  const [sending, setSending] = useState(false);

  if (view.loading) {
    return (
      <Shell>
        <Skeleton count={2} height={120} />
      </Shell>
    );
  }
  if (view.error || !view.data) {
    return (
      <Shell title={t('Квиз')}>
        <Empty icon="search" title={t('Квиз не найден')}>
          {view.error ?? t('Возможно, акция уже закончилась.')}
        </Empty>
      </Shell>
    );
  }

  const data: QuizView = view.data;
  const { campaign: c, questions, reward } = data;

  // Пройденный квиз сразу показывает итог: второй попытки нет, и прятать это
  // за кнопкой «начать» — обман.
  if (data.attempt && !result) {
    return (
      <Shell title={c.name}>
        <QuizScore
          correct={data.attempt.correct}
          total={data.attempt.total}
          reward={data.attempt.reward}
          replay
          campaignId={c.id}
        />
      </Shell>
    );
  }

  if (result) {
    return (
      <Shell title={c.name}>
        <QuizScore
          correct={result.correct}
          total={result.total}
          reward={result.reward}
          replay={result.replay}
          campaignId={c.id}
        />
        <Card style={{ marginTop: 14 }}>
          <div className="lab" style={{ marginBottom: 10 }}>
            {t('Разбор ответов')}
          </div>
          <div className="stack">
            {questions.map((q, i) => {
              const a = result.answers[i];
              if (!a) return null;
              return (
                <div key={i}>
                  <div className="qn">{t('Вопрос {n}', { n: i + 1 })}</div>
                  <div style={{ fontSize: 14, fontWeight: 510, margin: '4px 0 6px' }}>{q.text}</div>
                  <div className="row" style={{ gap: 8 }}>
                    <Badge tone={a.ok ? 'good' : 'bad'}>{a.ok ? t('верно') : t('не угадал')}</Badge>
                    <span className="sub">{q.options[a.answer]}</span>
                  </div>
                  {a.explain && <div className="expl">{a.explain}</div>}
                </div>
              );
            })}
          </div>
        </Card>
      </Shell>
    );
  }

  if (!started) {
    return (
      <Shell title={c.name}>
        <Card>
          <h1 className="h1">{c.name}</h1>
          <p className="sub" style={{ marginTop: 6 }}>
            {t('{qn} {questions} — {rn} {points} за участие.', {
              qn: questions.length,
              questions: plural(questions.length, 'вопрос', 'вопроса', 'вопросов'),
              rn: reward,
              points: plural(reward, 'балл', 'балла', 'баллов'),
            })}
          </p>
        </Card>

        <Card style={{ marginTop: 14 }}>
          <div className="row" style={{ marginBottom: 10, gap: 9 }}>
            <Icon name="scroll" size={16} />
            <span className="h2">{t('Что это за сделка')}</span>
          </div>
          <KeyRow label={t('Ты даёшь')}>
            {t('{n} ответа (обезличенно)', { n: questions.length })}
          </KeyRow>
          <KeyRow label={t('Ты получаешь')}>{t('{n} баллов', { n: reward })}</KeyRow>
          <KeyRow label={t('Бренд видит')}>{t('знание аудитории, не тебя')}</KeyRow>
        </Card>

        {signedIn ? (
          <div style={{ marginTop: 14 }}>
            <Button
              variant="pri"
              block
              onClick={() => {
                setStarted(true);
                setPicks([]);
                setCurrent(0);
              }}
            >
              {t('Начать квиз · +{n}', { n: reward })}
            </Button>
          </div>
        ) : (
          <Link to="/login" className="btn pri block" style={{ marginTop: 14 }}>
            {t('Войти, чтобы пройти')}
          </Link>
        )}

        <Card style={{ marginTop: 14 }}>
          <div className="row" style={{ gap: 9, alignItems: 'flex-start' }}>
            <Icon name="shield" size={16} />
            <span className="sub">
              {t('Правильные ответы зафиксированы заранее и одинаковы для всех — квиз нельзя подкрутить под человека. Сверяет их сервер, поэтому до ответа они не приходят в приложение.')}
            </span>
          </div>
        </Card>
      </Shell>
    );
  }

  const q = questions[current];
  if (!q) return null;

  const pick = async (option: number) => {
    const next = [...picks];
    next[current] = option;
    setPicks(next);

    if (current + 1 < questions.length) {
      setCurrent(current + 1);
      return;
    }

    setSending(true);
    try {
      setResult(await api.submitQuiz(c.id, next, linkFor(c.id)));
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('Не получилось отправить ответы'));
      setStarted(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <Shell title={c.name}>
      <div className="prog">
        {questions.map((_, i) => (
          <div key={i} className={`pseg ${i < current ? 'done' : i === current ? 'cur' : ''}`} />
        ))}
      </div>
      <Card>
        <div className="qn">
          {t('Вопрос {n} из {total}', { n: current + 1, total: questions.length })}
        </div>
        <div className="qt">{q.text}</div>
        {q.options.map((option, i) => (
          <button key={i} className="opt" disabled={sending} onClick={() => pick(i)}>
            <span className="mk">{String.fromCharCode(65 + i)}</span>
            {option}
          </button>
        ))}
      </Card>
    </Shell>
  );
}

/** Итог квиза: доля верных и начисленные баллы. */
function QuizScore({
  correct,
  total,
  reward,
  replay,
  campaignId,
}: {
  correct: number;
  total: number;
  reward: number;
  replay: boolean;
  campaignId: string;
}) {
  const plural = usePlural();
  const t = useT();
  const pct = total > 0 ? Math.round((100 * correct) / total) : 0;
  return (
    <Card style={{ textAlign: 'center' }}>
      <div
        className="disp"
        style={{ fontSize: 34, color: 'var(--head)', letterSpacing: '-0.03em' }}
      >
        {correct}/{total}
      </div>
      <div className="lab" style={{ marginTop: 2 }}>
        {t('верно')}
      </div>
      <h1 className="h1" style={{ marginTop: 12, fontSize: 21 }}>
        {pct >= 75 ? t('Отличный результат') : pct >= 50 ? t('Неплохо') : t('Есть куда расти')}
      </h1>
      <p className="sub" style={{ marginTop: 6 }}>
        {replay
          ? t('Квиз уже пройден — {n} {points} начислены тогда же. Вторая попытка не даётся.', {
              n: reward,
              points: plural(reward, 'балл', 'балла', 'баллов'),
            })
          : t('Начислено {n} {points} — за участие, независимо от числа верных.', {
              n: reward,
              points: plural(reward, 'балл', 'балла', 'баллов'),
            })}
      </p>
      <div className="row" style={{ marginTop: 14, gap: 8 }}>
        <Link to={`/c/${campaignId}`} className="btn out block">
          {t('К акции')}
        </Link>
        <Link to="/points" className="btn pri block">
          {t('Баллы')}
        </Link>
      </div>
    </Card>
  );
}

// ─── ежедневный вход ───

export function StreakScreen() {
  const plural = usePlural();
  const t = useT();
  const { id = '' } = useParams();
  const { signedIn, refresh } = useSession();
  const toast = useToast();
  const view = useResource(() => api.streakView(id), [id]);
  const [state, setState] = useState<StreakView | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (view.data) setState(view.data);
  }, [view.data]);

  if (!signedIn) {
    return (
      <NeedSignIn title={t('Ежедневный вход')}>
        {t('Серия привязана к аккаунту: без входа её некуда записывать.')}
      </NeedSignIn>
    );
  }
  if (view.loading && !state) {
    return (
      <Shell>
        <Skeleton count={2} height={120} />
      </Shell>
    );
  }
  if ((view.error && !state) || !state) {
    return (
      <Shell title={t('Ежедневный вход')}>
        <Empty icon="search" title={t('Акция не найдена')}>
          {view.error ?? t('Возможно, акция уже закончилась.')}
        </Empty>
      </Shell>
    );
  }

  const s = state;
  const check = async () => {
    setBusy(true);
    try {
      const result = await api.checkIn(s.campaign.id, linkFor(s.campaign.id));
      setState(result.view);
      void refresh();
      toast(
        result.replay
          ? t('Сегодня уже отмечено — награда начислена один раз')
          : t('+{n} {points} · день {day}', {
            n: result.reward,
            points: plural(result.reward, 'балл', 'балла', 'баллов'),
            day: result.day,
          }),
      );
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('Не получилось отметиться'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title={s.campaign.name}>
      <Card>
        <h1 className="h1">{s.campaign.name}</h1>
        <p className="sub" style={{ marginTop: 6 }}>
          {s.campaign.tagline || t('Заходи каждый день — награда растёт. Срыв не наказывается.')}
        </p>
      </Card>

      <div className="stats" style={{ marginTop: 14 }}>
        <div className="s">
          <div className="sv">{s.days}</div>
          <div className="sl">{t('дней отмечено')}</div>
        </div>
        <div className="s">
          <div className="sv mono">{num(s.earned)}</div>
          <div className="sl">{t('баллов собрано')}</div>
        </div>
        <div className="s">
          <div className="sv mono">{s.complete ? '—' : `+${s.next}`}</div>
          <div className="sl">{t('за следующий день')}</div>
        </div>
      </div>

      <Card style={{ marginTop: 14 }}>
        <div className="sgrid">
          {s.rewards.map((r, i) => {
            const claimed = i < s.days;
            const today = i === s.days;
            return (
              <div key={i} className={`sday ${claimed ? 'claimed' : ''} ${today ? 'today' : ''}`}>
                <div className="d">{t('Д{n}', { n: i + 1 })}</div>
                {claimed ? <Icon name="check" size={16} /> : <div className="r">+{r}</div>}
              </div>
            );
          })}
        </div>
      </Card>

      {s.complete ? (
        <Card style={{ marginTop: 14 }}>
          <div className="row" style={{ gap: 9 }}>
            <Icon name="trophy" size={16} />
            <span className="h2">{t('Все дни пройдены')}</span>
          </div>
          <p className="sub" style={{ marginTop: 6 }}>
            {t('Серия из {total} {days} собрана целиком — {earned} {points}.', {
              total: s.total,
              days: plural(s.total, 'дня', 'дней', 'дней'),
              earned: num(s.earned),
              points: plural(s.earned, 'балл', 'балла', 'баллов'),
            })}
          </p>
        </Card>
      ) : (
        <div style={{ marginTop: 14 }}>
          <Button variant="pri" block disabled={busy || s.today} onClick={check}>
            {s.today
              ? t('Сегодня отмечено · вернись завтра за +{n}', { n: s.next })
              : t('Забрать день {day} · +{n}', { day: s.next_day, n: s.next })}
          </Button>
        </div>
      )}

      <Card style={{ marginTop: 14 }}>
        <div className="row" style={{ gap: 9, alignItems: 'flex-start' }}>
          <Icon name="shield" size={16} />
          <span className="sub">
            {t('Пропустил день —')} <b>{t('серия не обнуляется в наказание')}</b>
            {t(': продолжишь с того же места. День считается по календарю акции, поэтому отметиться дважды за сутки нельзя даже с двух устройств.')}
          </span>
        </div>
      </Card>
    </Shell>
  );
}

// ─── приглашения ───

export function InviteScreen() {
  const t = useT();
  const { id = '' } = useParams();
  const { signedIn } = useSession();
  const toast = useToast();
  const view = useResource(() => api.inviteView(id), [id]);

  if (!signedIn) {
    return (
      <NeedSignIn title={t('Приведи друга')}>
        {t('Ссылка-приглашение привязана к аккаунту — иначе непонятно, кому начислять награду.')}
      </NeedSignIn>
    );
  }
  if (view.loading) {
    return (
      <Shell>
        <Skeleton count={2} height={120} />
      </Shell>
    );
  }
  if (view.error || !view.data) {
    return (
      <Shell title={t('Приведи друга')}>
        <Empty icon="search" title={t('Акция не найдена')}>
          {view.error ?? t('Возможно, акция уже закончилась.')}
        </Empty>
      </Shell>
    );
  }

  return <InviteBody data={view.data} onCopied={() => toast('Ссылка скопирована')} />;
}

function InviteBody({ data, onCopied }: { data: InviteView; onCopied: () => void }) {
  const plural = usePlural();
  const t = useT();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.link);
      onCopied();
    } catch {
      // Буфер может быть недоступен (нет https, отказ в разрешении) — ссылка
      // всё равно на экране и её можно выделить руками.
      onCopied();
    }
  };

  return (
    <Shell title={data.campaign.name}>
      <Card>
        <h1 className="h1">{data.campaign.name}</h1>
        <p className="sub" style={{ marginTop: 6 }}>
          {t('Награда обоим: +{you} тебе, +{friend} другу.', {
            you: data.you,
            friend: data.friend,
          })}
          {data.max > 0 &&
            ` ${t('До {n} {friends}.', {
              n: data.max,
              friends: plural(data.max, 'друга', 'друзей', 'друзей'),
            })}`}
        </p>
      </Card>

      <Card style={{ marginTop: 14 }}>
        <div className="lab" style={{ marginBottom: 8 }}>
          {t('Твоя ссылка-приглашение')}
        </div>
        <div className="refcode">
          <span className="c">{data.code}</span>
          <Button variant="out" size="sm" icon="copy" onClick={copy}>
            {t('копировать')}
          </Button>
        </div>
        <div className="sub mono" style={{ marginTop: 8, fontSize: 11.5 }}>
          {data.link}
        </div>
      </Card>

      <div className="stats" style={{ marginTop: 14 }}>
        <div className="s">
          <div className="sv">{data.invited}</div>
          <div className="sl">{t('перешли по ссылке')}</div>
        </div>
        <div className="s">
          <div className="sv mono">{data.qualified}</div>
          <div className="sl">{t('выполнили условие')}</div>
        </div>
        <div className="s">
          <div className="sv mono">{num(data.earned)}</div>
          <div className="sl">{t('баллов с цепи')}</div>
        </div>
      </div>

      <Card style={{ marginTop: 14 }}>
        <div className="h2" style={{ marginBottom: 6 }}>
          {t('Твоя цепочка')}
        </div>
        {data.chain.length === 0 ? (
          <p className="sub">
            {t('Пока никого. Отправь ссылку — друг получит {n} {points} сразу после первого участия.', {
              n: data.friend,
              points: plural(data.friend, 'балл', 'балла', 'баллов'),
            })}
          </p>
        ) : (
          <div className="chain">
            {data.chain.map((node, i) => (
              <div key={i} className={`cnode ${node.qualified ? 'active' : ''}`}>
                <span className="dot" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="nm">{node.name || t('участник')}</div>
                  <div className="st">
                    {node.qualified
                        ? t('выполнил условие · +{n} тебе', { n: data.you })
                        : t('перешёл, ждём участия')}
                  </div>
                </div>
                <span className="pts">{node.qualified ? `+${data.you}` : '—'}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card style={{ marginTop: 14 }}>
        <div className="row" style={{ gap: 9, alignItems: 'flex-start' }}>
          <Icon name="shield" size={16} />
          <span className="sub">
            {t('Награда начисляется, только когда друг')} <b>{t('реально поучаствовал')}</b> {t('в акции, а не в момент перехода по ссылке. Самоприглашения и второй пригласивший отклоняются сервером.')}
          </span>
        </div>
      </Card>
    </Shell>
  );
}

/** Переход по ссылке-приглашению: /invite/:code. */
export function InviteJoinScreen() {
  const t = useT();
  const { code = '' } = useParams();
  const { signedIn } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const [state, setState] = useState<'joining' | 'done' | 'error'>('joining');
  const [data, setData] = useState<InviteView | null>(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!signedIn) return;
    let alive = true;
    api
      .joinByInvite(code)
      .then((view) => {
        if (!alive) return;
        setData(view);
        setState('done');
        toast('Приглашение принято');
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setMessage(err instanceof ApiError ? err.message : t('Не получилось принять приглашение'));
        setState('error');
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, signedIn]);

  if (!signedIn) {
    // Код остаётся в адресе, поэтому после входа этот же экран доведёт дело
    // до конца — приглашение не теряется из-за того, что человек не вошёл.
    return (
      <NeedSignIn title={t('Приглашение')}>
        {t('Чтобы приглашение засчиталось другу, нужно войти — награда начисляется на аккаунт.')}
      </NeedSignIn>
    );
  }
  if (state === 'joining') {
    return (
      <Shell title={t('Приглашение')}>
        <Skeleton count={2} height={100} />
      </Shell>
    );
  }
  if (state === 'error' || !data) {
    return (
      <Shell title={t('Приглашение')}>
        <Empty icon="alert" title={t('Приглашение не принято')}>
          {message}
        </Empty>
        <div style={{ marginTop: 14 }}>
          <Button variant="out" block onClick={() => navigate('/')}>
            {t('К акциям')}
          </Button>
        </div>
      </Shell>
    );
  }
  return <InviteBody data={data} onCopied={() => toast('Ссылка скопирована')} />;
}
